import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, tokenStore, formatApiError } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { OrderHistory } from "../../components/OrderHistory";
import { ArrowLeft, MapPin, CalendarDays, Package, FileText, Download, X, UserCircle } from "lucide-react";

const fmtPrice = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("fr-FR"); } catch { return "-"; } };

export default function CompanyOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCancel = async () => {
    if (!window.confirm("Annuler cette commande ?")) return;
    setActing(true);
    try {
      await api.post(`/orders/${id}/cancel`);
      await load();
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  if (loading) return <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>;
  if (!order) return <div className="lc-card p-6">Commande introuvable.</div>;

  const cancellable = ["DRAFT", "PENDING_OTP"].includes(order.status);

  return (
    <div data-testid="company-order-detail">
      <Link to="/company/orders" className="text-xs text-[var(--lc-gray)] hover:text-[var(--lc-black)] inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Retour
      </Link>
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="font-mono text-xs text-[var(--lc-gray)]">{order.ref}</div>
          <h1 className="display text-3xl font-extrabold tracking-tight">Détail de commande</h1>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lc-card p-5 lg:col-span-2 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Info icon={UserCircle} label="Vendeur" value={order.vendor_name || "—"} />
            <Info icon={MapPin} label="Adresse de livraison" value={order.arrival_address} />
            <Info icon={CalendarDays} label="Date souhaitée" value={fmtDate(order.delivery_date_wanted)} />
            {order.delivery_date_confirmed && (
              <Info icon={CalendarDays} label="Date confirmée" value={fmtDate(order.delivery_date_confirmed)} highlight />
            )}
            <Info icon={Package} label="Colis hors gabarit" value={String(order.oversized_count)} />
            {order.purchase_order && (
              <a
                data-testid="company-po-download"
                href={`${API}/orders/${order.id}/purchase-order.pdf?token=${encodeURIComponent(tokenStore.get() || "")}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-bold text-[var(--lc-orange)] hover:underline inline-flex items-center"
              >
                Bon de commande PDF
              </a>
            )}
          </div>

          {order.recipient && (
            <div>
              <div className="lc-small-label mb-2">Destinataire</div>
              <div className="bg-[var(--lc-surface)] rounded-md p-3 text-sm space-y-1">
                <div className="font-semibold">{order.recipient.first_name} {order.recipient.last_name}</div>
                <div className="text-[var(--lc-gray)]">{order.recipient.address}</div>
                <div className="text-[var(--lc-gray)]">Tél : {order.recipient.phone}</div>
              </div>
            </div>
          )}
          {order.installer_name && (
            <div>
              <div className="lc-small-label mb-2">Poseur / monteur</div>
              <div className="bg-[var(--lc-surface)] rounded-md p-3 text-sm" data-testid="company-order-installer">
                <span className="font-semibold">{order.installer_name}</span>
                {order.installer_company && <span className="text-[var(--lc-orange)] font-semibold"> · {order.installer_company}</span>}
                {order.installer_phone && <span className="text-[var(--lc-gray)]"> · {order.installer_phone}</span>}
              </div>
            </div>
          )}
          {typeof order.kitchen_premounted === "boolean" && (
            <div>
              <div className="lc-small-label mb-2">Cuisine</div>
              <div className="bg-[var(--lc-surface)] rounded-md p-3 text-sm font-semibold" data-testid="company-order-kitchen">
                {order.kitchen_premounted ? "Prémontée" : "Non prémontée"}
              </div>
            </div>
          )}
          {typeof order.kitchen_paid_full === "boolean" && (
            <div
              data-testid="company-order-payment-status"
              className={`rounded-md p-3 text-sm font-semibold border ${order.kitchen_paid_full ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}
            >
              {order.kitchen_paid_full
                ? "Cuisine réglée à 100% par le client (déclaration vendeur)"
                : "Cuisine NON réglée à 100% par le client (déclaration vendeur)"}
            </div>
          )}
          {order.comment && (
            <div>
              <div className="lc-small-label mb-2">Commentaire</div>
              <p className="text-sm">{order.comment}</p>
            </div>
          )}

          {order.status === "DELIVERED" && order.delivery_proof?.bl_pdf_b64 && (
            <div className="border-t border-[var(--lc-border)] pt-5">
              <div className="lc-small-label mb-2 flex items-center gap-1.5">
                <FileText className="w-3 h-3 text-[var(--lc-orange)]" /> Bon de livraison
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`${API}/orders/${order.id}/bl.pdf?token=${encodeURIComponent(tokenStore.get() || "")}`}
                  target="_blank" rel="noreferrer"
                  data-testid="company-view-bl"
                  className="lc-btn-primary inline-flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" /> Voir le BL
                </a>
                <a
                  href={`${API}/orders/${order.id}/bl.pdf?token=${encodeURIComponent(tokenStore.get() || "")}`}
                  download={`BL-${order.ref}.pdf`}
                  data-testid="company-download-bl"
                  className="lc-btn-secondary inline-flex items-center gap-2"
                >
                  <Download className="w-4 h-4" /> Télécharger
                </a>
              </div>
            </div>
          )}

          {cancellable && (
            <div className="border-t border-[var(--lc-border)] pt-5">
              <button
                data-testid="company-cancel-order"
                onClick={onCancel}
                disabled={acting}
                className="lc-btn-secondary"
                style={{ color: "#DC2626", borderColor: "#FECACA" }}
              >
                <X className="w-4 h-4" /> Annuler cette commande
              </button>
            </div>
          )}

          <div className="border-t border-[var(--lc-border)] pt-5">
            <OrderHistory history={order.history} />
          </div>
        </div>

        <div className="lc-card p-5 h-fit">
          <div className="lc-small-label mb-1">Prix</div>
          <div className="text-3xl font-black text-[var(--lc-orange)]">{order.on_quote ? "Sur devis" : fmtPrice(order.total_ttc)}</div>
          {order.accounting_ref ? (
            <div className="text-xs text-[var(--lc-gray)] mt-1" data-testid="company-order-accounting-ref">
              Réf. comptable : <span className="font-mono font-bold text-[var(--lc-black)]">{order.accounting_ref}</span>
            </div>
          ) : (!order.on_quote && (
            <div className="text-xs font-semibold text-[var(--lc-orange)] mt-1" data-testid="company-order-accounting-ref-create">
              Créer un code contre marque
            </div>
          ))}
          {!order.on_quote && <div className="text-xs text-[var(--lc-gray)] mt-1">{fmtPrice(order.total_ht)} HT · TVA {(order.tva_rate * 100).toFixed(0)}%</div>}
          {!order.on_quote && order.purchase_price_ht != null && (
            <div className="mt-3 bg-[var(--lc-surface)] rounded-md p-2.5 text-xs space-y-1" data-testid="company-order-purchase-price">
              <div className="flex justify-between gap-3">
                <span className="text-[var(--lc-gray)]">Prix d'achat (LC Services)</span>
                <span className="font-bold whitespace-nowrap">{fmtPrice(order.purchase_price_ht)} HT</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-[var(--lc-gray)]">Prix de vente</span>
                <span className="font-bold whitespace-nowrap">{fmtPrice(order.total_ht)} HT / {fmtPrice(order.total_ttc)} TTC</span>
              </div>
            </div>
          )}
          {order.applied_options?.length > 0 && (
            <div className="mt-4 space-y-1 text-xs">
              <div className="lc-small-label">Détail (TTC)</div>
              <div className="flex justify-between"><span>Forfait base</span><span className="font-semibold">{fmtPrice(Math.round((order.base_price_ht || 0) * 1.2 * 100) / 100)}</span></div>
              {order.applied_options.map((o, i) => (
                <div key={i} className="flex justify-between text-[var(--lc-gray)]">
                  <span>+ {o.name}{o.quantity > 1 ? ` (×${o.quantity})` : ""}</span>
                  <span>{fmtPrice(Math.round((o.amount || 0) * 1.2 * 100) / 100)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value, highlight }) {
  return (
    <div className={highlight ? "bg-[var(--lc-orange-light)] p-3 rounded-md" : ""}>
      <div className="lc-small-label flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3 text-[var(--lc-orange)]" /> {label}
      </div>
      <div className="text-sm font-semibold">{value || "—"}</div>
    </div>
  );
}
